import { C3GraphicPlacement, C3_GRAPHIC_PLACEMENTS } from '../enums/c3-graphic-placement.enum';
import {
    C3GraphicResourceKind,
    C3GraphicResourceStatus,
} from '../enums/c3-graphic-resource-kind.enum';
import { C3GraphicResource } from '../models/c3-graphic-resource.model';

export interface C3GraphicPlacementArtwork {
    placement: C3GraphicPlacement;
    resources: C3GraphicResource[];
    originalResource?: C3GraphicResource;
    svgResource?: C3GraphicResource;
    previewResource?: C3GraphicResource;
    thumbnailResource?: C3GraphicResource;
    status?: C3GraphicResourceStatus;
}

export function c3GraphicPlacementArtwork(
    resources: readonly C3GraphicResource[],
    placement: C3GraphicPlacement,
): C3GraphicPlacementArtwork {
    const placementResources = resources.filter(resource => resource.placement === placement);
    const originalResource = placementResources
        .filter(resource => resource.kind === C3GraphicResourceKind.OriginalEps)
        .sort((left, right) => timestamp(right.createdOn) - timestamp(left.createdOn))[0];
    const rootId = originalResource && (originalResource.sourceResourceId || originalResource.id);
    const activeResources = rootId
        ? placementResources.filter(resource => (resource.sourceResourceId || resource.id) === rootId)
        : [];

    return {
        placement,
        resources: activeResources,
        originalResource,
        svgResource: findKind(activeResources, C3GraphicResourceKind.DerivedSvg),
        previewResource: findKind(activeResources, C3GraphicResourceKind.PreviewPng),
        thumbnailResource: findKind(activeResources, C3GraphicResourceKind.ThumbnailPng),
        status: originalResource?.status,
    };
}

export function c3GraphicPlacementArtworks(
    resources: readonly C3GraphicResource[],
): C3GraphicPlacementArtwork[] {
    return C3_GRAPHIC_PLACEMENTS.map(placement => c3GraphicPlacementArtwork(resources, placement));
}

export function isC3GraphicPlacementReady(artwork: C3GraphicPlacementArtwork): boolean {
    return !!artwork.originalResource
        && !!artwork.previewResource
        && [C3GraphicResourceStatus.Ready, C3GraphicResourceStatus.NeedsReview].includes(artwork.status!);
}

export function c3GraphicAggregateStatus(
    artworks: readonly C3GraphicPlacementArtwork[],
): C3GraphicResourceStatus {
    const statuses = artworks.filter(artwork => artwork.originalResource).map(artwork => artwork.status!);
    if (!statuses.length) return C3GraphicResourceStatus.Pending;
    for (const status of [
        C3GraphicResourceStatus.Rejected,
        C3GraphicResourceStatus.Failed,
        C3GraphicResourceStatus.Converting,
        C3GraphicResourceStatus.Validating,
        C3GraphicResourceStatus.Uploaded,
        C3GraphicResourceStatus.NeedsReview,
    ]) {
        if (statuses.includes(status)) return status;
    }
    return C3GraphicResourceStatus.Ready;
}

export function c3GraphicCanonicalFileName(
    code: string,
    placement: C3GraphicPlacement,
    extension = 'eps',
): string {
    return `${code.trim().toLowerCase()}-${placement.toLowerCase()}.${extension.replace(/^\./, '').toLowerCase()}`;
}

function findKind(
    resources: readonly C3GraphicResource[],
    kind: C3GraphicResourceKind,
): C3GraphicResource | undefined {
    return resources.find(resource => resource.kind === kind);
}

function timestamp(value: Date): number {
    const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
}
